
  import Vue from 'vue'

  import PropertyInfoDialog from '@/components/entities/property/PropertyInfoDialog.vue';
  import PropertyEditDialog from '@/components/entities/property/PropertyEditDialog.vue';
  import PropertyDeleteDialog from '@/components/entities/property/PropertyDeleteDialog.vue';
  import { objectsToCSV, filterItem } from '@/utils/Util';
import PropertyService from '@/services/PropertyService';

  export default Vue.extend({
    name: 'PropertiesTable',

    components: {
      PropertyInfoDialog,
      PropertyEditDialog,
      PropertyDeleteDialog
    },
    props: ['properties', 'loading'],
    data: (vm: any): any => ({
      tableLoadingText: vm.loadingText,
      search: "",
      showUUIDs: false,
      numberOfEntities: 0,
      selectedHeaders: [
        { text: vm.$t('attribute.name'), value: 'name', index: 100 },
        { text: vm.$t('attribute.identifier'), value: 'identifier', index: 300 },
        { text: vm.$t('attribute.street'), value: 'street', index: 400 },
        { text: vm.$t('attribute.updatedAt'), value: 'updatedAt', index: 1000 },
        { text: vm.$t('attribute.createdAt'), value: 'createdAt', index: 1100 },
      ],
      currentItems: [] as any[],
      loadingTags: false,
    }),
    async created() {
      if(this.properties.length === 0) {
        this.tableLoadingText = this.$t('property.noData') as string;
      }

      const columns = this.$root.$store.getters.selectedColumns('property');
      if(columns) {
        this.selectedHeaders = columns;
      }
    },
    watch: {
      selectedHeaders() {
        this.$store.commit("setSelectedColumns", { type: 'property', columns: this.selectedHeaders });
      } 
    },
    methods: {
      filterItem,
      onExport() {
          let filteredItems = this.properties;
          if(this.$refs.dataTable.$children[0].filteredItems) {
            filteredItems = this.$refs.dataTable.$children[0].filteredItems;
          }

          // Data structure
          const data = filteredItems.map((prop: any, index: number) => Object({
            uuid: prop.uuid,
            name: prop.name,
            aksId: prop.aksId,
            identifier: prop.identifier,
            street: prop.street,
            postalCode: prop.postalCode,
            city: prop.city,
            country: prop.country,
            createdAt: prop.createdAt,
            updatedAt: prop.updatedAt,
          }));

          const filename = 'bascloud_properties.csv';
          const csvContent = objectsToCSV(data);
          var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

          if ((navigator as any).msSaveBlob) { // IE 10+
              (navigator as any).msSaveBlob(blob, filename);
          } else {
              var link = document.createElement("a");
              if (link.download !== undefined) { // feature detection
                  // Browsers that support HTML5 download attribute
                  var url = URL.createObjectURL(blob);
                  link.setAttribute("href", url);
                  link.setAttribute("download", filename);
                  link.style.visibility = 'hidden';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
              }
          }
      },
      onReload() {
        this.$emit("reload");
      },
      onPropertyItemUpdate(item: any) {
        this.$emit("update", item);
      },
      onPropertyItemDelete(item: any) {
        this.$emit("delete", item);
      },
      onPaginationAndFilter(pagination: any) {
        this.numberOfEntities = pagination.itemsLength;
      },
      onTableRowClick(row: any) {
        const button: any = this.$refs["propertyInfoButton_" + row.uuid];
        // $el needed for native elements
        button.$el.click();
      },
      getTagsLabel(item: any, as_string=true) {
        if(!('tags' in item) && !this.loadingTags) {
          this.loadPropertyTags();
        }

        if(item?.tags && item.tags.length > 0) {
          if(as_string) {
            const text = item.tags.map((value: any) => value.name).sort().join(', ')
            return text
          } else {
            return item.tags.map((value: any) => value.name).sort()
          }
        } else {
          return [];
        }
      },
      async loadPropertyTags() {
        if(!this.loadingTags && this.currentItems.length > 0) {
          this.loadingTags = true;
          let promises: Promise<any>[] = [];
          this.currentItems.forEach(async (item: any) => {
            if(!('tags' in item))
              promises.push(this.loadTags(item));
          });
          await Promise.all(promises);
          this.loadingTags = false;
        }
      }, 
      async loadTags(item: any) {
        try {
            const tags = await PropertyService.getAssociatedTags(this.currentTenant, item.uuid);
            this.$set(item, 'tags', tags); // important else DOM is not updated
        } catch(err: any) {
            console.log(err);
            console.log(err.response);
            Vue.set(item, 'tags', []);
        }
      },
      onCurrentItemsChange(items: any[]) {
        this.currentItems = items;
      },
    },
    computed: {
      headers() {
        return [
            { text: this.$t('attribute.uuid'), value: 'uuid', index: 0 },
            { text: this.$t('attribute.name'), value: 'name', index: 100 },
            { text: this.$t('attribute.aksId'), value: 'aksId', index: 200 },
            { text: this.$t('attribute.identifier'), value: 'identifier', index: 300 },
            { text: this.$t('attribute.street'), value: 'street', index: 400 },
            { text: this.$t('attribute.postalCode'), value: 'postalCode', index: 500 },
            { text: this.$t('attribute.city'), value: 'city', index: 600 },
            { text: this.$t('attribute.country'), value: 'country', index: 700 },
            { text: this.$t('entity.tags'), value: 'tags', index: 800 },
            { text: this.$t('attribute.updatedAt'), value: 'updatedAt', index: 1000 },
            { text: this.$t('attribute.createdAt'), value: 'createdAt', index: 1100 },
        ];
      },
      loadingText(): any {
        return this.$t('property.loadingData');
      },
      computedTableHeader(): any {
        return [...this.selectedHeaders,
          { text: this.$t('attribute.actions'), value: 'actions', sortable: false, filterable: false, index: 9998 },
        ].sort((a: any, b: any) => a.index-b.index);
      },
      currentTenant(): any {
        return this.$root.$store.state.session.selectedTenant.uuid;
      },
      canUpdate(): any {
        if (this.$root.$store.state.session.permissions.resources && 'properties' in this.$root.$store.state.session.permissions.resources) {
          return this.$root.$store.state.session.permissions.resources['properties'].includes('PATCH');
        } else {
          return false;
        }
      },
      canDelete(): any {
        if (this.$root.$store.state.session.permissions.resources && 'properties' in this.$root.$store.state.session.permissions.resources) {
          return this.$root.$store.state.session.permissions.resources['properties'].includes('DELETE');
        } else {
          return false;
        }
      },
    },
  })
